import { Flex, Typography } from 'antd';
import React from 'react';
import { MessageOutlined } from '@ant-design/icons';
import useUser from 'utils/useUser';
import {
  useAddActivityComment,
  useDeleteActivityComment,
  useGenAiComment,
} from 'infrastructure/hooks/rpm';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import MessageItem from 'components/molecules/rpm-overview-table/components/reading-table/comments-cell/message-item';

import s from './styles.module.scss';
import ActivityCommentInput from './activity-comment-input';
import ActivityCommentList from './activity-comment-list';

import type { IActivityData } from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { QueryKey } from '@tanstack/react-query';
import type { TView } from 'components/molecules/activity-table/types';

const { Text } = Typography;

interface CommentSectionProps {
  activityData: IActivityData;
  idx: number | string;
  showReviewCol: boolean;
  getActivityQueryKey: QueryKey;
  view: TView;
  isExpanded: boolean;
  doNotCallRpm?: boolean;
  hideCommentInput?: boolean;
  setIsExpanded: (value: boolean) => void;
}

const CommentSection: React.FC<CommentSectionProps> = ({
  activityData,
  idx,
  showReviewCol,
  getActivityQueryKey,
  view,
  isExpanded,
  doNotCallRpm,
  hideCommentInput,
  setIsExpanded,
}) => {
  if (!activityData) return null;

  const { t } = useTranslation();

  const { isFullyManagedAgency, isAtRiskRole, isAgencyDoctor } = useUser();

  const {
    deviceId,
    id,
    createdAt,
    patient,
    isErroneous,
    value,
    statuses,
    readingType,
    globalStatus,
    thresholdName,
    erroneousReason,
    erroneousMarkedAt,
    erroneousAuthorName,
    comments,
  } = activityData;

  // This is temporary solution, delete it after new dashboard release
  const disableForFullyManagedAgency =
    view === 'dashboard' &&
    isFullyManagedAgency &&
    isAgencyDoctor &&
    isAtRiskRole;

  const { addActivityComment, InteractionStatusAlert } = useAddActivityComment({
    getActivityQueryKey,
    patientGuid: patient?.guid,
    tableRowIdx: idx,
  });

  const {
    data: aiData,
    genAiComment,
    isCommentGenerationLimitReached,
    isLoading,
  } = useGenAiComment({
    patientGuid: patient?.guid,
    tableRowIdx: idx,
  });

  const { deleteActivityComment } = useDeleteActivityComment({
    getActivityQueryKey,
    patientGuid: patient?.guid,
    tableRowIdx: idx,
  });

  const hasErroneousComment = isErroneous && !!erroneousReason;
  const totalCommentCount =
    (comments?.length ?? 0) + (hasErroneousComment ? 1 : 0);

  if (totalCommentCount === 0 && !isExpanded) {
    return null;
  }

  const disableCommentSectionExpand =
    (hideCommentInput && totalCommentCount === 0) || totalCommentCount === 0;

  const handleExpandClick = () => {
    if (disableCommentSectionExpand) return;

    setIsExpanded(!isExpanded);
  };

  return (
    <Flex vertical className={s['comment-section-wrapper']} gap={16}>
      <Flex
        align="center"
        gap={8}
        onClick={handleExpandClick}
        className={classNames(s['comment-header'], {
          [s['comment-header-no-expand']]: disableCommentSectionExpand,
        })}
      >
        <MessageOutlined />
        <Text className={s['comment-count-text']}>
          {t('labels.commentCount', { count: totalCommentCount })}
        </Text>
      </Flex>
      {isExpanded && (
        <>
          {isErroneous &&
            erroneousReason &&
            erroneousMarkedAt &&
            erroneousAuthorName && (
              <div className={s['erroneous-wrapper']}>
                <MessageItem
                  comment={{
                    message: erroneousReason,
                    type: 'text',
                    createdByFirstName: erroneousAuthorName,
                    createdByLastName: '',
                    createdByTitle: '',
                    createdByProfessionType: '',
                    createdAt: erroneousMarkedAt,
                    guid: 'erroneous-message',
                    callId: null,
                    reviewed: false,
                    pmpmStatus: null,
                  }}
                  isMockComment={false}
                  isErroneousComment
                />
              </div>
            )}
          {!hideCommentInput && (
            <ActivityCommentInput
              addComment={addActivityComment}
              genAiComment={genAiComment}
              deviceId={deviceId}
              readingId={id}
              idx={idx}
              key={`comment-col-${idx}`}
              createdAt={createdAt}
              showAiButton={showReviewCol}
              currentInteractionStatus={patient.interactionStatus}
              patientGuid={patient.guid}
              genAiCommentLoading={isLoading}
              isCommentGenerationLimitReached={isCommentGenerationLimitReached}
              aiData={aiData}
              isErroneous={isErroneous}
              thresholds={patient.threshold}
              values={value}
              statuses={statuses}
              readingType={readingType}
              globalStatus={globalStatus}
              thresholdName={thresholdName}
              disabled={disableForFullyManagedAgency}
              patientRpmDoNotCall={doNotCallRpm}
            />
          )}
          <ActivityCommentList
            deleteComment={deleteActivityComment}
            comments={comments}
            patientGuid={patient.guid}
            isErroneous={isErroneous === true}
          />
        </>
      )}
      {InteractionStatusAlert}
    </Flex>
  );
};

export default CommentSection;
